import { BadWearLevelling, volumeOf, wearLevelling } from "./wear-levelling.ts";

/**
 * A read-only FAT12, FAT16 and FAT32 reader over a volume's bytes, as FatFs
 * and ESP-IDF's fatfsgen.py write them: 8.3 names with the lower-case flags
 * of Windows NT, and long names.
 */

export interface FatEntry {
    name: string;
    directory: boolean;
    size: number;
    /** "1980-01-01 00:00:00": FAT keeps local time without a zone. */
    modified: string | undefined;
    /** The first cluster; 0 for an empty file. */
    cluster: number;
}

/** What `openFat` throws for bytes that hold no FAT volume. */
export class NotFat extends Error {}

const ATTR_DIRECTORY = 0x10;
const ATTR_VOLUME = 0x08;
const ATTR_LONG_NAME = 0x0f;
const ENTRY = 32;

export class FatVolume {
    readonly type: 12 | 16 | 32;
    readonly #bytes: Uint8Array;
    readonly #view: DataView;
    readonly #clusterSize: number;
    readonly #fatAt: number;
    /** FAT12 and FAT16 keep the root directory in a region of its own. */
    readonly #rootAt: number;
    readonly #rootLength: number;
    readonly #rootCluster: number;
    readonly #dataAt: number;
    readonly #clusters: number;

    constructor(bytes: Uint8Array) {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        if (bytes.length < 512 || view.getUint16(510, true) !== 0xaa55) throw new NotFat("no FAT boot sector");
        const sector = view.getUint16(11, true);
        const perCluster = bytes[13]!;
        const reserved = view.getUint16(14, true);
        const fats = bytes[16]!;
        const rootEntries = view.getUint16(17, true);
        const total = view.getUint16(19, true) || view.getUint32(32, true);
        const fatSize = view.getUint16(22, true) || view.getUint32(36, true);
        if (![512, 1024, 2048, 4096].includes(sector) || !isPowerOfTwo(perCluster) || reserved === 0) {
            throw new NotFat("the boot sector's parameters are not a FAT volume's");
        }
        if (fats < 1 || fats > 2 || fatSize === 0 || total === 0) throw new NotFat("the boot sector has no FAT");
        const rootSectors = Math.ceil((rootEntries * ENTRY) / sector);
        const dataSector = reserved + fats * fatSize + rootSectors;
        const clusters = Math.floor((total - dataSector) / perCluster);
        this.type = clusters < 4085 ? 12 : clusters < 65525 ? 16 : 32;
        if ((this.type === 32) !== (rootEntries === 0))
            throw new NotFat("the root directory does not fit the FAT type");
        if (total * sector > bytes.length)
            throw new NotFat(`the volume is ${total * sector} bytes, more than the ${bytes.length} read`);
        this.#bytes = bytes;
        this.#view = view;
        this.#clusterSize = perCluster * sector;
        this.#fatAt = reserved * sector;
        this.#rootAt = (reserved + fats * fatSize) * sector;
        this.#rootLength = rootSectors * sector;
        this.#rootCluster = this.type === 32 ? view.getUint32(44, true) : 0;
        this.#dataAt = dataSector * sector;
        this.#clusters = clusters;
    }

    /** The entries of the root directory. */
    root(): FatEntry[] {
        if (this.type === 32) return entries(this.#chain(this.#rootCluster));
        return entries(this.#bytes.subarray(this.#rootAt, this.#rootAt + this.#rootLength));
    }

    list(directory: FatEntry): FatEntry[] {
        if (!directory.directory) throw new Error(`${directory.name} is not a folder`);
        // A ".." entry that points at the root holds cluster 0.
        return directory.cluster === 0 ? this.root() : entries(this.#chain(directory.cluster));
    }

    /** A file's bytes; a file whose chain ends early is cut short. */
    read(file: FatEntry): Uint8Array {
        if (file.directory) throw new Error(`${file.name} is a folder`);
        if (file.size === 0) return new Uint8Array(0);
        return this.#chain(file.cluster, file.size).slice(0, file.size);
    }

    /** The clusters of a chain, joined, up to `limit` bytes. */
    #chain(first: number, limit = Infinity): Uint8Array {
        const parts: Uint8Array[] = [];
        let length = 0;
        const seen = new Set<number>();
        for (
            let cluster = first;
            cluster >= 2 && cluster < this.#clusters + 2 && length < limit;
            cluster = this.#next(cluster)
        ) {
            if (seen.has(cluster)) break;
            seen.add(cluster);
            const at = this.#dataAt + (cluster - 2) * this.#clusterSize;
            parts.push(this.#bytes.subarray(at, at + this.#clusterSize));
            length += this.#clusterSize;
        }
        const joined = new Uint8Array(length);
        let at = 0;
        for (const part of parts) {
            joined.set(part, at);
            at += part.length;
        }
        return joined;
    }

    /** The FAT's entry for a cluster: the next one, or a value past the last cluster at the chain's end. */
    #next(cluster: number): number {
        switch (this.type) {
            case 12: {
                const at = this.#fatAt + cluster + (cluster >> 1);
                const pair = this.#view.getUint16(at, true);
                return cluster & 1 ? pair >> 4 : pair & 0xfff;
            }
            case 16:
                return this.#view.getUint16(this.#fatAt + cluster * 2, true);
            case 32:
                return this.#view.getUint32(this.#fatAt + cluster * 4, true) & 0x0fffffff;
        }
    }
}

export interface FatPartition {
    /** Whether ESP-IDF's wear-levelling layer holds the volume, or the partition is the volume. */
    layout: "wear-levelled" | "plain";
    volume: FatVolume;
}

/**
 * The FAT volume in a partition's bytes: behind the wear-levelling layer when
 * the partition ends with its configuration, else from the partition's start.
 * Throws `NotFat` with the reason when there is none.
 */
export function openFatPartition(partition: Uint8Array): FatPartition {
    let wl;
    try {
        wl = wearLevelling(partition);
    } catch (e) {
        if (e instanceof BadWearLevelling) throw new NotFat(e.message);
        throw e;
    }
    if (wl !== undefined) return { layout: "wear-levelled", volume: new FatVolume(volumeOf(wl, partition)) };
    if (partition.subarray(0, 0x1000).every((byte) => byte === 0xff)) throw new NotFat("the partition is erased");
    return { layout: "plain", volume: new FatVolume(partition) };
}

/** The entries of a directory's bytes, without "." and "..", the volume label, and deleted entries. */
function entries(bytes: Uint8Array): FatEntry[] {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const found: FatEntry[] = [];
    let long: { parts: string[]; checksum: number } | undefined;
    for (let at = 0; at + ENTRY <= bytes.length; at += ENTRY) {
        const first = bytes[at]!;
        if (first === 0) break;
        const attributes = bytes[at + 11]!;
        if (first === 0xe5) {
            long = undefined;
            continue;
        }
        if (attributes === ATTR_LONG_NAME) {
            // Long-name entries come last part first, each with 13 UTF-16 units.
            if (first & 0x40) long = { parts: [], checksum: bytes[at + 13]! };
            long?.parts.unshift(longPart(view, at));
            continue;
        }
        const short = bytes.subarray(at, at + 11);
        const name =
            long !== undefined && long.checksum === checksum(short)
                ? long.parts.join("")
                : shortName(short, bytes[at + 12]!);
        long = undefined;
        if (attributes & ATTR_VOLUME || name === "." || name === "..") continue;
        found.push({
            name,
            directory: (attributes & ATTR_DIRECTORY) !== 0,
            size: view.getUint32(at + 28, true),
            modified: timestamp(view.getUint16(at + 24, true), view.getUint16(at + 22, true)),
            cluster: (view.getUint16(at + 20, true) << 16) | view.getUint16(at + 26, true),
        });
    }
    return found;
}

function longPart(view: DataView, at: number): string {
    const units: number[] = [];
    for (const offset of [1, 3, 5, 7, 9, 14, 16, 18, 20, 22, 24, 28, 30]) {
        const unit = view.getUint16(at + offset, true);
        if (unit === 0 || unit === 0xffff) break;
        units.push(unit);
    }
    return String.fromCharCode(...units);
}

/** The 8.3 name; bits 3 and 4 of the NT byte mark a lower-case base and extension. */
function shortName(short: Uint8Array, nt: number): string {
    const text = (bytes: Uint8Array) => String.fromCharCode(...bytes).trimEnd();
    let base = text(short.subarray(0, 8));
    let extension = text(short.subarray(8, 11));
    if (short[0] === 0x05) base = `å${base.slice(1)}`;
    if (nt & 0x08) base = base.toLowerCase();
    if (nt & 0x10) extension = extension.toLowerCase();
    return extension === "" ? base : `${base}.${extension}`;
}

function checksum(short: Uint8Array): number {
    let sum = 0;
    for (const byte of short) sum = (((sum & 1) << 7) + (sum >> 1) + byte) & 0xff;
    return sum;
}

function timestamp(date: number, time: number): string | undefined {
    if (date === 0) return undefined;
    const pad = (n: number) => String(n).padStart(2, "0");
    const day = `${1980 + (date >> 9)}-${pad((date >> 5) & 0xf)}-${pad(date & 0x1f)}`;
    return `${day} ${pad(time >> 11)}:${pad((time >> 5) & 0x3f)}:${pad((time & 0x1f) * 2)}`;
}

function isPowerOfTwo(n: number): boolean {
    return n > 0 && (n & (n - 1)) === 0;
}
